import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {AngularFireAuth} from '@angular/fire/auth';
import {Subscription, of, timer, BehaviorSubject, Observable, from, throwError, defer, ReplaySubject} from 'rxjs';
import {catchError, mergeMap, shareReplay, switchMap, take, map, finalize, timeout, throwIfEmpty} from 'rxjs/operators';
import {Subject} from "rxjs";
import {plainToClass} from "class-transformer";

import {AngularFireFunctions} from "@angular/fire/functions";
import {Token} from "./messages/Token";
import {IAuthProfile} from "../../domain/auth/IAuthProfile";
import {NGXLogger} from "ngx-logger";
import {isEqual} from "lodash";
import {Role} from "../../domain/user/Role";
import {WaihonaUserService} from "../WaihonaUserService";
import * as firebase from 'firebase/app';
import {WaihonaUser} from "../../domain/user/WaihonaUser";
import Auth0Client from "@auth0/auth0-spa-js/dist/typings/Auth0Client";
import {Auth0Client as SPAAuth0Client, LogoutOptions} from "@auth0/auth0-spa-js";
import {environment} from "../../../environments/environment";
import {WaihonaUserRepository} from "../repository/WaihonaUserRepository";

@Injectable()
export class AuthFunctions {

	public _currentUser$:BehaviorSubject<WaihonaUser> = new BehaviorSubject(null);
	public _isAuthenticatingCurrently$:BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public _isAuthenticated$:BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public firebaseAuthUser:Subject<firebase.User> = new Subject<firebase.User>();

	// Create subject of user profile data
	public userProfileSubject$ = new BehaviorSubject<any>(null);

	public _options = {
		client_id: environment.auth.clientId,
		domain: environment.auth.clientDomain,
		responseType: 'token',

		redirect_uri: `${window.location.origin}/#/callback`, //environment.auth.redirect,
		audience: environment.auth.audience,
		scope: environment.auth.scope
	};

    // Construct once, but restore explicitly. A failed silent login must not poison client creation.
    public auth0Client$:Observable<Auth0Client> = defer(() => of(new SPAAuth0Client({
        client_id: environment.auth.clientId,
        domain: environment.auth.clientDomain,
        redirect_uri: `${window.location.origin}/#/callback`,
        audience: environment.auth.audience,
        scope: environment.auth.scope,
        useRefreshTokens: true
    }))).pipe(shareReplay(1));

    public readonly sessionTimeoutMs:number = 15000;
    private pendingSession$:Observable<boolean>;
    private pendingResult:ReplaySubject<boolean>;
    private sessionSubscription:Subscription;
    private sessionGeneration = 0;
    public lastAuthenticationError:string = null;

	public authProfile:IAuthProfile;

	public loggedInFirebase:boolean = false;

	// Subscribe to Firebase token renewal timer stream
	public refreshTokenOnTimeoutSub:Subscription;

	// Subscribe to the Firebase token refresh on user update stream
	public refreshTokenOnUserUpdateSub:Subscription;
	protected currentUserRoles:Array<Role>;

	protected _isInTokenAuth$:BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public _isHandlingLocalSelfAuth$:BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

	public prevAuthState:any = null;
	public currentAuthState:any = null;
	public freshLogin:boolean = null;
	public updatesScheduled:boolean = null;

	constructor(
		private router:Router,
		private afAuth:AngularFireAuth,
		private functions:AngularFireFunctions,
		protected logger:NGXLogger,
		protected waihonaUserService:WaihonaUserService,
		protected waihonaUserRepo:WaihonaUserRepository
	) {}

	public isInTokenAuth$():BehaviorSubject<boolean> {
		return this._isInTokenAuth$;
	}

	public isInTokenAuth():boolean {
		return this._isInTokenAuth$.getValue();
	}


    public setAuth0Session(authResult:any, profile:IAuthProfile):Observable<IGetCustomTokenResponse> {
        this.authProfile = profile;
        if (Number.isFinite(authResult?.expiresIn)) {
            localStorage.setItem('expires_at', String(Date.now() + authResult.expiresIn * 1000));
        }
        return this.getCustomToken();
    }

    private getToken$():Observable<Token> {
        return defer(() => {
            this._isInTokenAuth$.next(true);
            return this.functions.httpsCallable('getToken')({user:this.authProfile});
        }).pipe(take(1), map((result:any) => {
            if (!result?.token || !result?.waihonaUser?.guid) {
                throw new Error('The sign-in service returned an incomplete account.');
            }
            const token = plainToClass<Token, any>(Token, result) as any;
            token.waihonaUser.guid = result.waihonaUser.guid;
            return token;
        }), finalize(() => this._isInTokenAuth$.next(false)));
    }

    private getCustomToken():Observable<IGetCustomTokenResponse> {
        return this.getToken$().pipe(switchMap(token => this.signInWithCustomToken(token).pipe(
            map(signedIn => ({token, signedIn}))
        )));
    }

    private signInWithCustomToken(token:Token):Observable<boolean> {
        return defer(() => from(firebase.auth().signInWithCustomToken(token.token))).pipe(map(() => true));
    }

	public scheduleFirebaseRenewal() {
		this.logger.info("AuthService::scheduleFirebaseRenewal");
		// Check for existing Firebase subscription and unsubscribe,
		this.unscheduleFirebaseRenewal();
		// If user isn't authenticated, then return (don't schedule renewal)
		if (!this.loggedInFirebase) {
			console.log("not scheduling firebase renewal");
			return;
		}

		// Create and subscribe to expiration observable
		// Custom Firebase tokens minted by Firebase
		// expire after 3600 seconds (1 hour)
		const expiresAt = new Date().getTime() + (3600 * 1000);
		const expiresIn$ = of(expiresAt)
			.pipe(
				mergeMap(
					expires => {
						const now = Date.now();
						// Use timer to track delay until expiration
						// to run the refresh at the proper time
						return timer(Math.max(1, expires - now));
					}
				)
			);

		this.refreshTokenOnTimeoutSub = expiresIn$
			.subscribe(
				() => {
					this.logger.info('Firebase token expired; fetching a new one');
                    this.refreshToken();
				}
			);
	}

    /** One eager shared attempt for startup, role changes and renewal; every outcome settles. */
    public refreshToken(options?):Observable<boolean> {
        if (this.pendingSession$) { return this.pendingSession$; }
        const result = new ReplaySubject<boolean>(1);
        const pending = result.asObservable();
        this.pendingSession$ = pending;
        this.pendingResult = result;
        const generation = ++this.sessionGeneration;
        this._isAuthenticatingCurrently$.next(true);
        this.lastAuthenticationError = null;
        this.sessionSubscription = this.auth0Client$.pipe(take(1),
            switchMap(client => defer(() => from(client.checkSession())).pipe(
                switchMap(() => from(client.getUser(options)))
            )),
            switchMap((user:IAuthProfile) => {
                if (!user) { throw new Error('Your session could not be restored. Please log in again.'); }
                return this.setAuth0Session({}, user);
            }),
            timeout(this.sessionTimeoutMs),
            take(1),
            throwIfEmpty(() => new Error('Session restoration returned no result.')),
            map(response => {
                if (!response.signedIn) { throw new Error('Firebase sign-in did not complete.'); }
                this.loggedInFirebase = true;
                this._currentUser$.next(response.token.waihonaUser);
                this._isAuthenticated$.next(true);
                this.userProfileSubject$.next(this.authProfile);
                this.scheduleFirebaseRenewal();
                this.refreshTokenOnUserRoleUpdate(response.token.waihonaUser.guid);
                this.updatesScheduled = true;
                return true;
            }),
            catchError(error => {
                this.logger.error('Session restoration failed', error);
                this.clearSessionState();
                this.lastAuthenticationError = 'Please log in again to continue.';
                return of(false);
            })
        ).subscribe(success => {
            if (generation !== this.sessionGeneration) { return; }
            this.pendingSession$ = null;
            this._isInTokenAuth$.next(false);
            this._isAuthenticatingCurrently$.next(false);
            this._isHandlingLocalSelfAuth$.next(false);
            result.next(success);
            result.complete();
        });
        return pending;
    }

    /** Cancel pending app work on explicit logout/cancel; no automatic Auth0 redirect on failure. */
    public cancelAuthentication():void {
        ++this.sessionGeneration;
        if (this.sessionSubscription) { this.sessionSubscription.unsubscribe(); }
        this.pendingSession$ = null;
        this.clearSessionState();
        this._isInTokenAuth$.next(false);
        this._isAuthenticatingCurrently$.next(false);
        this._isHandlingLocalSelfAuth$.next(false);
        if (this.pendingResult) { this.pendingResult.next(false); this.pendingResult.complete(); }
    }

    private clearSessionState():void {
        this.unscheduleFirebaseRenewal();
        if (this.refreshTokenOnUserUpdateSub) { this.refreshTokenOnUserUpdateSub.unsubscribe(); }
        this.currentUserRoles = null;
        this.updatesScheduled = false;
        this.loggedInFirebase = false;
        this._currentUser$.next(null);
        this.userProfileSubject$.next(null);
        this._isAuthenticated$.next(false);
    }

	// Unsubscribe from previous expiration observable
	public unscheduleFirebaseRenewal() {
		this.logger.info("AuthService::unscheduleFirebaseRenewal");
		if (this.refreshTokenOnTimeoutSub) {
			this.refreshTokenOnTimeoutSub.unsubscribe();
		}
	}

	public refreshTokenOnUserRoleUpdate(userId:string) {
		this.logger.info("AuthService::refreshTokenOnUserRoleUpdate");

		// Unsubscribe from previous refresh token observable
		if (this.refreshTokenOnUserUpdateSub) {
			this.refreshTokenOnUserUpdateSub.unsubscribe();
		}

		// If user isn't authenticated, then return (don't schedule renewal)
		if (!this.loggedInFirebase) {
			return;
		}

		this.refreshTokenOnUserUpdateSub = this.waihonaUserService.repo.watch$(userId).subscribe((waihonaUser:WaihonaUser) => {
			if (waihonaUser != null) {
				this._currentUser$.next(waihonaUser);

				let previousRoles = this.currentUserRoles;
				if (!isEqual(previousRoles, waihonaUser.roles)) {
					this.currentUserRoles = waihonaUser.roles;
					if (!!previousRoles) {
						console.log("Updating user token!");
						//TODO: Send some sort of nice update to user
						this.refreshToken();
					}
				}
			}
		});
	}

	public logout(auth0LogoutOptions?:LogoutOptions) {
        this.cancelAuthentication();
		// Call firebase log out method
		this.firebaseLogout().then(() => {
			this.logOutCleanup();
			// then call Auth0 logout
			this.auth0Logout(auth0LogoutOptions);
		});
	}

	public auth0Logout(auth0LogoutOptions?:LogoutOptions) {
		this.logger.info("AuthFunctions::auth0Logout");
		this.updatesScheduled = false;

		let options:LogoutOptions;
		// if logout options were passed in, use those
		if (auth0LogoutOptions) {
			options = auth0LogoutOptions;
		} else {
			// otherwise use the default options
			options = {
				client_id: this._options.client_id,
				returnTo: `${window.location.origin}`
			}
		}

		// Ensure Auth0 client instance exists
		this.auth0Client$.take(1).subscribe((client:Auth0Client) => {
			// then call Auth0 logout
			client.logout(options);
		});
	}

	private logOutCleanup() {
		this.currentUserRoles = null;
		this._currentUser$.next(null);
		this._isAuthenticated$.next(false);
	}

	public firebaseLogout():Promise<void> {
		this.logger.info("AuthFunctions::firebaseLogout");
		// Ensure all auth items removed
		localStorage.removeItem('isLoggedIn');
		localStorage.removeItem('expires_at');
		localStorage.removeItem('auth_redirect');

		this.loggedInFirebase = false;

		return firebase.auth().signOut().then(()=> {
			this.firebaseAuthUser.next(null);
			this.logger.info("firebase logout succeeded");
		}, (failure)=> {
			this.logger.error("firebase logout failed");
		});
	}

	public get isTokenValid():boolean {
		this.logger.info("AuthFunctions::isTokenValid");
		// Check if current time is past access token's expiration
		const expiresAt = Number(localStorage.getItem('expires_at'));
		return Date.now() < expiresAt;
	}

}

export interface IGetCustomTokenResponse {
	token: Token, signedIn: boolean
}
